<template>
  <footer class="report-footer">
    <DivPrintableBackground ref="bg">
      <h3>Looking for more?</h3>
      <ul>
        <template v-if="!isSelectedSchoolPK">
          <li>Read about <LinkExternalUrl url="/resources/2024-equity.html">equity, diversity, and inclusion</LinkExternalUrl> on the {{ schoolYearRange }} Snapshot</li>
          <li><LinkExternalUrl :url="panoramaURL">View responses to all of the NYC School Survey questions</LinkExternalUrl> for the {{schoolYearRange}} school year</li>

<!-- Removed for 2022 Unofficial and Official, but may be reversed next year-->
          <li>Find <!--additional data on the <LinkExternalUrl :url="guideURL">School Quality Guide</LinkExternalUrl>, and -->multi-year data on the <LinkExternalUrl :url="dashboardURL">School Performance Dashboard</LinkExternalUrl></li>

          <template v-if="!isSelectedSchoolCharter">
            <li v-if="!hasQRReview">Read about the <LinkExternalUrl url="https://www.schools.nyc.gov/about-us/reports/school-quality/quality-review">Quality Review process</LinkExternalUrl>.</li>
            <li v-else>Read the complete <LinkExternalUrl :url="qualityReviewURL">Quality Review Report (PDF)</LinkExternalUrl></li>
          </template>

          <li v-if="selectedSchoolType==='HS'">Apply to high schools online at <LinkExternalUrl :url="schoolFinderURL">MySchools</LinkExternalUrl></li>
          <li v-else-if="selectedSchoolType==='MS'">Apply online to middle schools at <LinkExternalUrl url="https://www.myschools.nyc/en/schools/middle-school/">MySchools</LinkExternalUrl></li>
          <li v-else-if="['EC','ES'].indexOf(selectedSchoolType)>-1">Apply online to kindergarten at <LinkExternalUrl url="https://www.myschools.nyc/en/schools/kindergarten/">MySchools</LinkExternalUrl></li>
          <li v-else-if="selectedSchoolType==='K-8'">Apply online through the <LinkExternalUrl url="https://www.myschools.nyc/en/schools/kindergarten/">MySchools kindergarten directory</LinkExternalUrl> or the <LinkExternalUrl url="https://www.myschools.nyc/en/schools/middle-school/">MySchools middle school directory</LinkExternalUrl></li>
          <li v-else-if="['YABC','HST'].indexOf(selectedSchoolType)>-1">Find <LinkExternalUrl url="https://www.schools.nyc.gov/enrollment/other-ways-to-graduate/transfer-high-schools">admissions information for transfer high schools</LinkExternalUrl></li>

          <!-- For 2024, EC and D75 won't have Scoring Guide PDF, so the string below shouldn't show for these two school types -->
          <li v-if="selectedSchoolType && ['EC', 'D75'].indexOf(selectedSchoolType)<0 && !isEmbargoed">See details about how this school’s ratings were calculated on the <LinkExternalUrl :url="scoringGuideURL">School Quality Scoring Guide (PDF)</LinkExternalUrl></li>
          <!-- No link for D75 -->
        </template>
        <template v-else>
          <li>Find pre-K sites and apply online at <LinkExternalUrl url="https://www.myschools.nyc/en/schools/pre-k/">MySchools</LinkExternalUrl></li>
          <li>Find <LinkExternalUrl url="https://www.schools.nyc.gov/enrollment/enroll-grade-by-grade/pre-k">pre-K admissions information</LinkExternalUrl></li>
          <li>Find out more about the <LinkExternalUrl url="http://NYCSchoolSurvey.org">NYC School Survey</LinkExternalUrl></li>
        </template>
      </ul>

      <template v-if="hasSelectedSchool && !isSelectedSchoolPK">
        <div class="key-title">Key:</div>
        <FrameworkRatingsLegend/>
      </template>

      <template v-if="!isSelectedSchoolPK">
        <div class="asterisk-footer"><strong>Note: </strong>City, borough, and district comparisons are made to schools of the same school type.</div>
        <div class="asterisk-footer" v-if="!isCovidYear"><span class="asterisk">*</span>The Comparison Group shows how similar students performed at other schools throughout the city.</div>
        <!-- SQRT-11528: figured out all unscored questions are on INFO tab or D75 IP which has no rating anyway so removed this footnote <div class="asterisk-footer"><span class="asterisk">**</span>Results from this survey question do not contribute to the overall rating of this section.</div> -->
        <!--
        <div class="asterisk-footer" v-if="['D75','MS', 'HS', 'HST'].indexOf(selectedSchoolType)===-1 && selectedSchoolType"><span class="asterisk">***</span>This information is from the {{ yearRange(reportYear-1) }} school year.</div>
        -->
<!-- Removed for 2022 Unofficial and Official, but may be reversed next year
        <div class="asterisk-footer" v-if="['HS','HST'].indexOf(selectedSchoolType)>-1 && isCovidYear"><span class="asterisk">**</span>Some Regents exams were administered but are not included in this report due to COVID-19.</div>
-->
      </template>
      <template v-else>
        <div class="asterisk-footer"><span class="asterisk">*</span>Children fall within the age range for a given grade if they turn that age by December 31st.</div>
        <!-- SQRT-8958
        <div class="asterisk-footer"><span class="asterisk">**</span>Head Start provides high-quality comprehensive services to income-eligible toddlers and preschoolers and their families, supporting school readiness, health and wellness, and family empowerment.</div>
        -->
        <div class="asterisk-footer"><span class="asterisk">**</span>Special classes serve children with IEPs and high needs in small ratio settings. </div>
      </template>
    </DivPrintableBackground>
  </footer>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'
import Component from 'vue-class-component'

import { NS_REPORT_GROUP, NS_CURRENT_REPORT, REPORT_YEAR } from './const'
import { yearRangeForReportYear } from '@/utils/data'

import DivPrintableBackground from '@/components/DivPrintableBackground'
import LinkExternalUrl from '@/components/LinkExternalUrl'
import FrameworkRatingsLegend from './FrameworkRatingsLegend'

@Component({
  computed: {
    ...mapGetters(NS_REPORT_GROUP, [
      'schoolYearRange'
    ]),
    ...mapGetters(NS_CURRENT_REPORT, [
      'hasSelectedSchool',
      'selectedSchool',
      'selectedSchoolType',
      'isSelectedSchoolCharter',
      'isSelectedSchoolPK',
      'isEmbargoed',
      'hasQRReview',
      'schoolFinderURL',
      'qualityReviewURL',
      // 'guideURL',
      'scoringGuideURL',
      'panoramaURL',
      'dashboardURL',
      'isCovidYear'
    ])
  },
  components: {
    FrameworkRatingsLegend,
    DivPrintableBackground,
    LinkExternalUrl
  },
  methods: {
    yearRange: yearRangeForReportYear
  }
})
export default class ReportFooter extends Vue {
  /* Lifecycle */
  updated () {
    this.updateBGSize()
  }
  reportYear = REPORT_YEAR
  /* Getters */

  /* Methods - utility */

  updateBGSize () {
    // When content repopulates for print URLs, need to update background size
    //  However, this does not update calculation based on CSS content: rules
    const bg = this.$refs.bg
    if (bg) {
      bg.$_RootSizeMixin_updateInnerSize()
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import './css/const.scss';
$legend-margin-left: 0;

.div-printable-bg {
  background-color: $button-footer-header-bg;
  border-color: $button-footer-header-bg;
  padding: 0px;
  /deep/ .content {
    padding: 24px;
    > * {
      display: block;
    }
  }
}
ul {
  margin-bottom: 0px;
  padding-left: 24px;
}
.report-footer h3 {
  color: black;
  border-bottom: 0px;
  margin: 0px;
}
.asterisk-footer {
  padding-top: 10px;
  &+ .asterisk-footer {
    padding-top: 0;
  }
  text-indent: -22px;
  margin-left: 23px;
  span.asterisk {
    width: 20px;
    display: inline-block;
    text-align: right;
    padding-right: 2px;
  }
}
.key-title {
  display: block;
  margin-top: 15px;
  margin-left: $legend-margin-left;
}

.report-footer {
.framework-ratings-legend {
  display: block;
  height: auto;
  border: none;
  padding: 0;
  margin-left: $legend-margin-left;
  /deep/ .legend-item {
    display: inline-block;
    vertical-align: top;
    &:not(:last-child) {
      margin-right: 36px;
    }
    .viz-four-bar-rating {
      display: block;
      .bar {
        fill: black;
        &.hollow {
          fill: $button-footer-header-bg;
        }
      }
    }
    .name {
      display: block;
      max-width: 72px;
      color: black;
    }
  }
}
}
/* Responsive rules */

/* update 956px */
@media screen and (max-width: $screen-width-2-column-small) {
.div-printable-bg /deep/ .v-outer {
  margin: 0px 10px;
  width: calc(100% - 20px);
  .content {
    padding-left: 0px;
    padding-right: 0px;
  }
}
}

/* upto 644px */
@media screen and (max-width: $screen-width-1-column-large) {
.div-printable-bg /deep/ .v-outer {
  max-width: $max-width-1-col;
  margin: auto;
}
}

/* upto 538px */
@media screen and (max-width: $screen-width-1-column-medium) {
.div-printable-bg /deep/ .v-outer {
  max-width: none;
}
}

/* upto 456px */
@media screen and (max-width: $screen-width-1-column-small) {
  .report-footer {
    .framework-ratings-legend {
      /deep/ .legend-item {
        &:not(:last-child) {
          margin-right: 24px;
        }
      }
    }
  }
}

</style>



// WEBPACK FOOTER //
// src/components/sqs/2024/ReportFooter.vue